import { FC, useState } from 'react'
import { CampaignsOverviewData } from 'src/services/__generated__/api'
import { Cards } from './Cards'
import { PlatformBreakdownView } from './PlatformBreakdownView'
import {
  PlatformSentimentData,
  SentimentDistributionChart,
} from './SentimentDistributionChart'
import styles from './heroSection.module.scss'

type ViewMode = 'general' | 'platform'

interface HeroSectionProps {
  isLoading: boolean
  overview?: CampaignsOverviewData
  platformSentimentData?: PlatformSentimentData[]
  sourcesList?: string[]
  topSourcesData?: {
    name?: string
    negativePercentage?: number
    negatives?: number
    neutralPercentage?: number
    neutrals?: number
    positivePercentage?: number
    positives?: number
    total?: number
  }[]
}

const ratioToPercent = (ratio?: number): number =>
  ratio === undefined || ratio === null ? 0 : Math.round(ratio * 100)

export const HeroSection: FC<HeroSectionProps> = ({
  isLoading,
  overview,
  platformSentimentData,
  sourcesList = [],
  topSourcesData = [],
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('general')
  const mentions = overview?.mentions
  const totalMentions = mentions?.total || 0

  return (
    <div className={styles.heroSection}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>Mentions Overview</span>
        <div className={styles.toggleContainer}>
          <button
            className={`${styles.toggleButton} ${viewMode === 'general' ? styles.toggleButtonActive : ''}`}
            onClick={() => setViewMode('general')}
            type="button"
          >
            General Overview
          </button>
          <button
            className={`${styles.toggleButton} ${viewMode === 'platform' ? styles.toggleButtonActive : ''}`}
            onClick={() => setViewMode('platform')}
            type="button"
          >
            Platform Breakdown
          </button>
        </div>
      </div>

      {viewMode === 'general' && (
        <div className={styles.cardsWrapper}>
          <Cards
            activeCampaigns={overview?.campaigns?.active || 0}
            isLoading={isLoading}
            mentions={{
              negativeCount: mentions?.negative || 0,
              negativePercentage: ratioToPercent(mentions?.negativeRatio),
              neutralCount: mentions?.neutral || 0,
              neutralPercentage: ratioToPercent(mentions?.neutralRatio),
              positiveCount: mentions?.positive || 0,
              positivePercentage: ratioToPercent(mentions?.positiveRatio),
              total: totalMentions,
            }}
            sourceCounts={topSourcesData}
            sourcesList={sourcesList}
            topCampaign={overview?.topCampaign}
            topKeyword={overview?.topKeyword}
            topSource={overview?.sources}
            totalCampaigns={overview?.campaigns?.total || 0}
          />
        </div>
      )}

      {viewMode === 'platform' && (
        <div className={styles.cardsWrapper}>
          <PlatformBreakdownView
            isLoading={isLoading}
            overallMentions={overview?.mentions?.total ?? 0}
            sources={topSourcesData}
          />
        </div>
      )}

      <div className={styles.sentimentSection}>
        <h3 className={styles.sentimentTitle}>
          Sentiment Distribution Per Platform
        </h3>
        <div className={styles.sentimentChartWrapper}>
          <SentimentDistributionChart
            data={platformSentimentData ?? []}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  )
}
